import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchListDetail, fetchListItems } from '../utils/listHandler';
import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import { getUserInfoByUsersubCF, UserData } from '../utils/userProfileApi';
import type { ListItem, UserList, ListOpenLevel } from '../types/rlTypes';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CardActionArea from '@mui/material/CardActionArea';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Link from '@mui/material/Link';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useAppTokens } from '../context/ThemeModeContext';

type SortOrder = 'added_at' | 'pub_year';

const openLevelLabel = (level: ListOpenLevel | undefined): string => {
  switch (level) {
    case 'public': return '公開';
    case 'url_share': return 'リンク共有';
    case 'friends': return '友達のみ';
    case 'private':
    default: return '非公開';
  }
};

const ListPage: React.FC = () => {
  const TOKENS = useAppTokens();
  const { bookListID } = useParams<{ bookListID: string }>();
  const { idToken } = useAuth();
  const navigate = useNavigate();
  const [listDetail, setListDetail] = useState<UserList | null>(null);
  const [items, setItems] = useState<ListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);
  const [userInfo, setUserInfo] = useState<UserData | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('added_at');

  const loadList = useCallback(async (token: string | null, listId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const [detail, itemsRes] = await Promise.all([
        fetchListDetail(listId, token),
        fetchListItems(listId, token),
      ]);

      if (!isMounted.current) return;

      setListDetail(detail);
      setItems(itemsRes.items ?? []);

      if (detail?.user_sub) {
        const userData = await getUserInfoByUsersubCF(detail.user_sub);
        if (isMounted.current) setUserInfo(userData);
      }
    } catch (err: any) {
      if (!isMounted.current) return;
      const status = err?.response?.status;
      if (status === 404) {
        setError('リストが見つかりませんでした。');
      } else if (status === 401 || status === 403) {
        setError('このリストを表示する権限がありません。');
      } else {
        setError('リストの読み込み中にエラーが発生しました。');
      }
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;

    if (!bookListID) {
      setError('リスト ID が指定されていません。');
      setIsLoading(false);
      return () => { isMounted.current = false; };
    }

    // idTokenが無くても（未ログインでも）とりあえず取得を試みる。公開リストなら
    // バックエンドが認証不要で返してくれる。ログイン処理中で少し遅れてidTokenが
    // 届いた場合は、useEffectの依存配列によりこの効果が再実行され、本人の権限で
    // 再取得される（以前はsetIntervalでポーリングしていたが、コールバック内の
    // idTokenがエフェクト実行時点のクロージャに固定され更新されないバグがあり、
    // 未ログイン時は必ず「認証情報の取得に失敗しました」になっていた）
    loadList(idToken ?? null, bookListID);

    return () => { isMounted.current = false; };
  }, [bookListID, idToken, loadList]);

  const handleBookClick = (rlBookId: number) => {
    window.open(`/book/${rlBookId}`, '_blank');
  };

  const handleSortChange = (event: SelectChangeEvent<SortOrder>) => {
    setSortOrder(event.target.value as SortOrder);
  };

  const sortedItems = React.useMemo(() => {
    const arr = [...items];
    if (sortOrder === 'pub_year') {
      arr.sort((a, b) => (b.pub_year ?? 0) - (a.pub_year ?? 0));
    } else {
      // added_at DESC
      arr.sort((a, b) => {
        const at = a.user_list_item_added_at ? new Date(a.user_list_item_added_at).getTime() : 0;
        const bt = b.user_list_item_added_at ? new Date(b.user_list_item_added_at).getTime() : 0;
        return bt - at;
      });
    }
    return arr;
  }, [items, sortOrder]);

  if (isLoading) {
    return (
      <Box sx={{ mt: 7, minHeight: 'calc(100vh - 56px)', background: TOKENS.bg }}>
        <Container maxWidth="md" sx={{ pt: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ color: TOKENS.textSub }} />
          <Typography variant="body2" sx={{ mt: 2, color: TOKENS.textSub }}>
            データを読み込んでいます...
          </Typography>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 7, minHeight: 'calc(100vh - 56px)', background: TOKENS.bg }}>
        <Container maxWidth="md" sx={{ pt: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }

  if (!listDetail) {
    return (
      <Box sx={{ mt: 7, minHeight: 'calc(100vh - 56px)', background: TOKENS.bg }}>
        <Container maxWidth="md" sx={{ pt: 4 }}>
          <Alert severity="info">リストデータがありません。</Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 7, minHeight: 'calc(100vh - 56px)', background: TOKENS.bg }}>
    <Container maxWidth="md" sx={{ pt: 4, pb: 4 }}>
      {userInfo && (
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar
            src={userInfo.profileImage}
            alt={userInfo.username}
            sx={{ width: 56, height: 56, cursor: 'pointer' }}
            onClick={() => navigate(`/${userInfo.username}`)}
          />
          <Box ml={2}>
            <Typography variant="h6" component="span" sx={{ fontSize: '1.25rem', fontWeight: 'bold', color: TOKENS.text }}>
              {userInfo.preferred_name}
            </Typography>
            <Typography variant="body2" sx={{ color: TOKENS.textSub }}>
              <Link
                component="button"
                variant="body2"
                sx={{ color: TOKENS.textSub }}
                onClick={() => navigate(`/${userInfo.username}`)}
              >
                @{userInfo.username}
              </Link>
            </Typography>
          </Box>
        </Box>
      )}
      <Typography variant="h4" gutterBottom sx={{ color: TOKENS.text }}>{listDetail.user_list_name}</Typography>
      <Typography variant="body2" gutterBottom sx={{ color: TOKENS.textSub }}>
        公開設定: {openLevelLabel(listDetail.user_list_open_level)}
      </Typography>
      {listDetail.user_list_description && (
        <Typography variant="body1" paragraph sx={{ color: TOKENS.text }}>{listDetail.user_list_description}</Typography>
      )}
      <FormControl sx={{ mb: 2 }}>
        <InputLabel id="sort-label">並び替え</InputLabel>
        <Select
          labelId="sort-label"
          value={sortOrder}
          onChange={handleSortChange}
          label="並び替え"
        >
          <MenuItem value="added_at">登録順</MenuItem>
          <MenuItem value="pub_year">出版順</MenuItem>
        </Select>
      </FormControl>
      {sortedItems.length === 0 ? (
        <Alert severity="info">このリストには本がまだありません。</Alert>
      ) : (
        <Grid container spacing={2}>
          {sortedItems.map((book) => {
            const cover = generateCoverUrlSmart(book.rl_book_id, book.title, book.isbn13);
            return (
              <Grid item xs={12} sm={6} md={4} lg={2} key={book.rl_book_id}>
                <Card>
                  <CardActionArea onClick={() => handleBookClick(book.rl_book_id)}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={cover}
                      alt={book.title ?? ''}
                      sx={{ objectFit: 'contain', p: 1 }}
                    />
                  </CardActionArea>
                </Card>
                <Box sx={{ mt: 1, textAlign: 'left' }}>
                  <Typography variant="subtitle2" noWrap sx={{ color: TOKENS.text }}>{book.title}</Typography>
                  <Typography variant="body2" noWrap sx={{ color: TOKENS.textSub }}>{book.author}</Typography>
                  <Typography variant="caption" display="block" noWrap sx={{ color: TOKENS.textSub }}>
                    {book.pub_year ? `${book.pub_year}` : ''}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
    </Box>
  );
};

export default ListPage;
